(() => {
  if (customElements.get('dz-email-capture')) return;
  const DAY = 86400000;
  const keys = { dismissed: 'dozzi-email-dismissed', subscribed: 'dozzi-email-subscribed', pending: 'dozzi-email-pending' };
  function read(storage, key) {
    try { return JSON.parse(window[storage].getItem(key)); } catch { return null; }
  }
  function write(storage, key, value) {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch { /* Signup works without storage. */ }
  }
  function recent(timestamp, duration) {
    return typeof timestamp === 'number' && timestamp <= Date.now() && Date.now() - timestamp < duration;
  }

  customElements.define('dz-email-capture', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      const forms = [...this.querySelectorAll('form')];
      const pending = read('sessionStorage', keys.pending);
      const returnedForm = forms.find(form => location.hash === `#${form.id}` ||
        (pending?.id === form.id && recent(pending.at, 30 * 60000)));
      const success = this.querySelector('[data-email-success]');
      if (success) write('localStorage', keys.subscribed, Date.now());

      forms.forEach(form => {
        form.addEventListener('submit', event => {
          if (event.defaultPrevented) return;
          write('sessionStorage', keys.pending, { id: form.id, at: Date.now() });
          const button = form.querySelector('[type="submit"]');
          if (!button) return;
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          // Allow a retry if a platform challenge cancels or delays navigation.
          clearTimeout(this.submitTimer);
          this.submitTimer = setTimeout(() => this.resetButtons(), 10000);
        }, options);
      });
      window.addEventListener('pageshow', () => this.resetButtons(), options);
      if (!this.dialog) return;
      this.querySelectorAll('[data-email-open]').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => this.open(), options);
      });
      this.querySelectorAll('[data-email-close]').forEach(button => {
        button.addEventListener('click', () => this.dismiss(), options);
      });
      this.dialog.addEventListener('cancel', () => this.rememberDismissal(), options);
      this.dialog.addEventListener('close', () => this.rememberDismissal(), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button:not(:disabled), input:not([type="hidden"]):not(:disabled), a[href], [tabindex="0"]')]
          .filter(element => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
      this.dialog.addEventListener('click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.dismiss();
      }, options);
      this.setupScratch(options);
      if (this.dataset.preview === 'true' && new URLSearchParams(location.search).get('welcome') === 'preview') this.open();
      document.addEventListener('shopify:section:select', event => {
        if (event.target.contains(this)) this.open();
      }, options);
      const response = returnedForm?.querySelector('[data-email-success], [data-email-error]');
      if (response && this.dialog.contains(returnedForm)) {
        this.reveal(false);
        this.open();
        response.focus();
      } else if (response) {
        response.focus();
      }
      if (response) write('sessionStorage', keys.pending, null);
      if (!this.suppressed() && !this.dialog.open && this.dataset.designMode !== 'true' && this.dataset.pageType !== 'cart') {
        this.timer = setTimeout(() => this.autoOpen(), (Number(this.dataset.delay) || 18) * 1000);
      }
    }

    suppressed() {
      return this.dataset.subscriber === 'true' || Boolean(this.querySelector('[data-email-success]')) ||
        recent(read('localStorage', keys.subscribed), 180 * DAY) ||
        recent(read('localStorage', keys.dismissed), (Number(this.dataset.dismissDays) || 7) * DAY);
    }

    autoOpen() {
      if (this.suppressed() || !this.isConnected) return;
      if (document.hidden || document.querySelector('dialog[open]') || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]') || document.querySelector('.mobile-panel.open')) {
        this.timer = setTimeout(() => this.autoOpen(), 3000);
        return;
      }
      this.open();
    }

    open() {
      clearTimeout(this.timer);
      if (!this.dialog.open) this.dialog.showModal();
      this.paintScratch();
    }

    rememberDismissal() {
      clearTimeout(this.timer);
      write('localStorage', keys.dismissed, Date.now());
    }

    dismiss() {
      // Save before close's deferred event, including immediate navigation away.
      this.rememberDismissal();
      this.dialog.close();
    }

    reveal(focus, animate = false) {
      if (this.revealed) return;
      this.revealed = true;
      const stage = this.querySelector('[data-email-reveal-stage]');
      this.querySelector('[data-email-reward]').removeAttribute('aria-hidden');
      stage.classList.add('is-revealed');
      const canvas = this.querySelector('[data-email-scratch]');
      canvas.tabIndex = -1;
      canvas.setAttribute('aria-hidden', 'true');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const showForm = () => {
        const content = this.querySelector('.dz-email-offer');
        const before = content.getBoundingClientRect().height;
        const form = this.querySelector('[data-email-popup-form]');
        form.hidden = false;
        this.querySelector('[data-email-claim-copy]').hidden = false;
        if (animate && !reducedMotion && this.dialog.open) {
          const after = content.getBoundingClientRect().height;
          content.animate([{ height: `${before}px`, overflow: 'hidden' }, { height: `${after}px`, overflow: 'hidden' }], { duration: 480, easing: 'cubic-bezier(.2,.7,.2,1)' });
          form.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 400, easing: 'ease-out' });
        }
        if (focus || document.activeElement === canvas) this.dialog.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      };
      if (animate && !reducedMotion) this.revealTimer = setTimeout(showForm, 1600);
      else showForm();
    }

    paintScratch() {
      const canvas = this.querySelector('[data-email-scratch]');
      if (!canvas || this.scratchPainted) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * scale);
      canvas.height = Math.round(rect.height * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      const colors = getComputedStyle(this.dialog);
      const gold = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gold.addColorStop(0, '#d6ab46');
      gold.addColorStop(.25, '#efcf79');
      gold.addColorStop(.52, '#b39740');
      gold.addColorStop(.76, '#ead18b');
      gold.addColorStop(1, '#ae963c');
      ctx.fillStyle = gold;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Grain is painted into the erasable cover, not over the revealed offer.
      const foil = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let seed = 1729;
      for (let i = 0; i < foil.data.length; i += 4) {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        const grain = ((seed >>> 24) / 255 - .5) * 30;
        for (let channel = 0; channel < 3; channel++) foil.data[i + channel] += grain;
      }
      ctx.putImageData(foil, 0, 0);
      ctx.strokeStyle = '#806c252e';
      ctx.lineWidth = canvas.width * .06;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const x = canvas.width * (.23 + i * .09);
        ctx.moveTo(x, canvas.height * (.73 + (i % 2) * .05));
        ctx.lineTo(x + canvas.width * .11, canvas.height * (.25 + (i % 3) * .035));
      }
      ctx.stroke();
      ctx.fillStyle = '#fff1bb';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `700 ${30 * scale}px ${colors.fontFamily}`;
      ctx.fillText('SCRATCH HERE', canvas.width / 2, canvas.height / 2, canvas.width - 32 * scale);
      this.scratchPainted = true;
    }

    setupScratch(options) {
      const canvas = this.querySelector('[data-email-scratch]');
      if (!canvas) return;
      let previous = null;
      const checkProgress = () => {
        if (this.revealed) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let clear = 0;
        let sampled = 0;
        for (let i = 3; i < pixels.length; i += 16) {
          sampled++;
          if (pixels[i] < 128) clear++;
        }
        if (clear / sampled >= .25) this.reveal(false, true);
      };
      const position = event => {
        const rect = canvas.getBoundingClientRect();
        return { x: (event.clientX - rect.left) * canvas.width / rect.width, y: (event.clientY - rect.top) * canvas.height / rect.height };
      };
      const draw = event => {
        const ctx = canvas.getContext('2d');
        if (!ctx || !previous || this.revealed) return;
        const point = position(event);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = '#171717';
        ctx.lineWidth = 34 * canvas.width / canvas.getBoundingClientRect().width;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(previous.x, previous.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        previous = point;
        if (!this.progressTimer) {
          this.progressTimer = setTimeout(() => {
            this.progressTimer = null;
            checkProgress();
          }, 50);
        }
      };
      canvas.addEventListener('pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.paintScratch();
        canvas.setPointerCapture(event.pointerId);
        previous = position(event);
        draw(event);
      }, options);
      canvas.addEventListener('pointermove', event => {
        if (canvas.hasPointerCapture(event.pointerId)) draw(event);
      }, options);
      const finish = event => {
        if (!previous) return;
        previous = null;
        if (event.pointerId != null && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
        checkProgress();
      };
      canvas.addEventListener('pointerup', finish, options);
      canvas.addEventListener('pointercancel', finish, options);
      let keyboardRow = 0;
      canvas.addEventListener('keydown', event => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const rect = canvas.getBoundingClientRect();
        const y = Math.min(18 + keyboardRow++ * 26, rect.height - 8);
        previous = { x: 0, y: y * canvas.height / rect.height };
        draw({ clientX: rect.right, clientY: rect.top + y });
        finish({});
      }, options);
    }

    resetButtons() {
      this.querySelectorAll('button[aria-busy]').forEach(button => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.submitTimer);
      clearTimeout(this.revealTimer);
      clearTimeout(this.progressTimer);
      if (this.dialog?.open) this.dialog.close();
    }
  });
})();
